// src/extenders/variant-switcher.js
var VariantSwitcher = class extends HTMLElement {
  /**
   * @property {NodeList} optionFieldsets - Collection of variant option fieldsets
   * @property {HTMLInputElement} variantInput - Hidden input for selected variant ID
   */
  constructor() {
    super();
    this.initializeElements();
    this.initializeState();
    this.bindEvents();
  }
  /**
   * Initialize DOM element references
   */
  initializeElements() {
    this.formRoot = this.closest("product-form") || this;
    this.elements = {
      optionFieldsets: this.querySelectorAll(".option-fieldset"),
      variantInput: this.querySelector('input[name="id"]'),
      addToCartButton: this.formRoot.querySelector("#add-to-cart-button"),
      variantInputs: this.querySelectorAll('input[type="radio"]')
    };
    this.sectionId = this.dataset.sectionId;
  }
  findQuickAddModalRoot() {
    let currentElement = this;
    while (currentElement) {
      if (currentElement.classList) {
        const hasQuickAddClass = Array.from(currentElement.classList).some((className) => className.startsWith("js-quick-add-modal-"));
        if (hasQuickAddClass) {
          return currentElement;
        }
      }
      currentElement = currentElement.parentElement;
    }
    return null;
  }
  getContextRoot() {
    const quickAddModal = this.findQuickAddModalRoot();
    if (quickAddModal) {
      return quickAddModal;
    }
    if (this.sectionId) {
      const shopifySection = document.getElementById(`shopify-section-${this.sectionId}`);
      if (shopifySection) {
        return shopifySection;
      }
      const sectionById = document.getElementById(this.sectionId);
      if (sectionById) {
        return sectionById;
      }
    }
    return this.formRoot;
  }
  getProductSelector() {
    const productId = this.formRoot.getAttribute("data-product-id");
    return productId ? `[data-product-id="${productId}"]` : "";
  }
  updateQuickAddPrice(variantInput) {
    var _a;
    const quickAddModal = this.findQuickAddModalRoot();
    if (!quickAddModal) {
      return;
    }
    const quickAddPriceHtml = (_a = variantInput == null ? void 0 : variantInput.dataset) == null ? void 0 : _a.quickAddPriceHtml;
    if (!quickAddPriceHtml) {
      return;
    }
    const productSelector = this.getProductSelector();
    const target = quickAddModal.querySelector(`.quick-add-price-holder-element${productSelector}`);
    if (target) {
      target.innerHTML = quickAddPriceHtml;
    }
  }
  /**
   * Initialize component state
   */
  initializeState() {
    this.optionState = {};
    this.elements.optionFieldsets.forEach((fieldset) => {
      const fieldsetName = fieldset.getAttribute("name");
      const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
      this.optionState[fieldsetName] = (checkedInput == null ? void 0 : checkedInput.value) || "";
    });
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get("variant");
    if (variantId) {
      this.preSelectVariant(variantId);
    }
  }
  /**
   * Bind event listeners
   */
  bindEvents() {
    this.elements.optionFieldsets.forEach((fieldset) => {
      fieldset.addEventListener("change", this.handleOptionChange.bind(this));
    });
  }
  /**
   * Pre-selects variant options based on URL params
   * @param {string} variantId - Variant ID from URL
   */
  preSelectVariant(variantId) {
    const input = this.querySelector(`input[data-variant-id="${variantId}"]`);
    if (input) {
      input.checked = true;
      this.handleOptionChange({ target: input });
    }
  }
  /**
   * Handles option selection changes
   * @param {Event} event - Change event
   */
  handleOptionChange(event) {
    this.optionState[event.target.name] = event.target.value;
    this.updateQuickAddPrice(event.target);
    if (this.areAllOptionsSelected()) {
      this.elements.variantInput.value = event.target.dataset.variantId;
      this.updateVariant(event);
    }
  }
  /**
   * Checks if all variant options are selected
   * @returns {boolean}
   */
  areAllOptionsSelected() {
    return !Object.values(this.optionState).includes("");
  }
  /**
   * Toggles loading state for variant selection
   * @param {boolean} isLoading - Whether variant is being switched
   */
  toggleLoadingState(isLoading) {
    this.elements.variantInputs.forEach((input) => {
      input.disabled = isLoading;
      input.parentElement.setAttribute("aria-busy", isLoading.toString());
    });
    this.elements.addToCartButton.disabled = isLoading;
  }
  /**
   * Updates the product variant sections
   */
  async updateVariant(event) {
    this.toggleLoadingState(true);
    const url = new URL(window.location.origin + event.target.dataset.href);
    url.searchParams.set("section_id", this.sectionId);
    try {
      const responseText = await fetch(url.toString()).then((r) => r.text());
      const parser = new DOMParser();
      const dom = parser.parseFromString(responseText, "text/html");
      const productSelector = this.getProductSelector();
      const quickAddPriceSelector = `.quick-add-price-holder-element${productSelector}`;
      let fallbackDom = null;
      if (this.findQuickAddModalRoot() && !dom.querySelector(quickAddPriceSelector)) {
        const fullPageUrl = new URL(window.location.origin + event.target.dataset.href);
        const fullPageText = await fetch(fullPageUrl.toString()).then((r) => r.text());
        fallbackDom = parser.parseFromString(fullPageText, "text/html");
      }
      this.updateSections(dom, fallbackDom);
      this.updateURL(event.target.dataset.variantId);
    } catch (error) {
      console.error("Error updating variant:", error);
    } finally {
      this.toggleLoadingState(false);
    }
  }
  /**
   * Updates relevant DOM sections with new variant content
   * @param {Document} dom - Parsed response HTML
   */
  updateSections(dom, fallbackDom = null) {
    const alternativeCheckbox = this.formRoot.querySelector("#alternative-product");
    const wasChecked = alternativeCheckbox == null ? void 0 : alternativeCheckbox.checked;
    const productSelector = this.getProductSelector();
    const sections = [
      `.product-price-holder-element${productSelector}`,
      `.quick-add-price-holder-element${productSelector}`,
      "#stock-pop-up",
      "#add-to-cart-button",
      "#keep_me_informed-button",
      "#cart-item-quantity",
      "#alternative-product-toggle"
    ];
    const contextRoot = this.getContextRoot();
    sections.forEach((selector) => {
      const source = dom.querySelector(selector) || (fallbackDom == null ? void 0 : fallbackDom.querySelector(selector));
      const target = this.formRoot.querySelector(selector) || contextRoot.querySelector(selector);
      if (source && target) {
        target.outerHTML = source.outerHTML;
      }
    });
    const newAlternativeCheckbox = this.formRoot.querySelector("#alternative-product");
    if (newAlternativeCheckbox && wasChecked !== void 0) {
      newAlternativeCheckbox.checked = wasChecked;
    }
    this.initializeElements();
    document.dispatchEvent(new CustomEvent("variant-updated"));
  }
  /**
   * Updates URL with selected variant
   */
  updateURL(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set("variant", variantId);
    window.history.replaceState({}, "", url.toString());
  }
};
customElements.define("variant-switcher", VariantSwitcher);
